import z from 'zod';

import Constants from '@/constants';

import {
  ComplianceCourseEnrollmentDto,
  Plan,
  UserPlanStatus,
} from '@/_generated/api-clients/xp';

const str = z.string().optional().catch(undefined);
const num = z.number().optional().catch(undefined);
const bool = z.boolean().nullish().catch(undefined);

const planVmSchema = z
  .object({
    // Plan-native fields (1:1)
    activeCertificates: z.number().catch(0),
    allowTrainTogether: z.boolean().catch(false),
    assignedUsers: z.number().catch(0),
    autoAssign: z.boolean().catch(false),
    category: str,
    completedUsers: z.number().catch(0),
    content: z.unknown().optional().catch(undefined),
    created: str,
    createdBy: str,
    dueDateOffsetInDays: num,
    expireAfterCompletionOffsetInDays: num,
    id: str,
    legacyId: str,
    modified: str,
    modifiedBy: str,
    name: z.record(z.string(), z.string()).optional().catch(undefined),
    ownerId: z.string().catch(''),
    ownerType: z
      .enum(['SUPPORT_CENTER', 'LOCATION', 'OPERATOR', 'SELF'])
      .catch('SELF'),
    permissions: z.unknown().optional().catch(undefined),
    readonly: z.boolean().catch(false),
    reassignBeforeExpirationOffsetInDays: num,
    totalSteps: num,
    type: z.enum(['STORE', 'TEMPLATE', 'COMPLIANCE', 'ANCHOR']).catch('STORE'),

    // Alternate names (ChecklistDto / ComplianceCourseEnrollmentDto / etc.)
    courseID: str,
    courseId: str,
    courseName: z.record(z.string(), z.string()).optional().catch(undefined),
    createdDate: str,
    disabled: bool,
    enabled: bool,
    estimatedMinutes: num,
    estimatedTimeToCompleteInMinutes: num,
    lastModified: str,
    lastModifiedBy: str,
    pathwayCourseId: str,
    status: z.enum(['ACTIVE', 'INACTIVE']).optional().catch(undefined),
    stepsTotal: num,

    // Nested checklist (from AssignableChecklistResult / ProcessedManagedPlan)
    checklist: z
      .object({
        category: str,
        created: str,
        createdBy: str,
        disabled: bool,
        estimatedMinutes: num,
        id: str,
        lastModified: str,
        lastModifiedBy: str,
        name: z.record(z.string(), z.string()).optional().catch(undefined),
        ownerId: str,
        ownerType: z
          .enum(['OPERATOR', 'LOCATION', 'SUPPORT_CENTER'])
          .optional()
          .catch(undefined),
        permissions: z.unknown().optional().catch(undefined),
        stepsTotal: num,
        templateId: str,
      })
      .optional()
      .catch(undefined),
  })
  .transform(
    ({
      checklist,
      courseID,
      courseId,
      courseName,
      createdDate,
      disabled,
      enabled,
      estimatedMinutes,
      lastModified,
      lastModifiedBy,
      pathwayCourseId,
      stepsTotal,
      ...f
    }) => ({
      ...f,
      content: f.content as Plan['content'],
      created: f.created ?? createdDate ?? checklist?.created ?? '',
      createdBy: f.createdBy ?? checklist?.createdBy ?? '',
      estimatedTimeToCompleteInMinutes:
        f.estimatedTimeToCompleteInMinutes ??
        estimatedMinutes ??
        checklist?.estimatedMinutes,
      id:
        f.id ?? pathwayCourseId ?? courseID ?? courseId ?? checklist?.id ?? '',
      modified: f.modified ?? lastModified ?? checklist?.lastModified ?? '',
      modifiedBy:
        f.modifiedBy ?? lastModifiedBy ?? checklist?.lastModifiedBy ?? '',
      name: f.name ?? courseName ?? checklist?.name ?? {},
      ownerId: f.ownerId ? f.ownerId : (checklist?.ownerId ?? ''),
      ownerType:
        f.ownerType !== 'SELF'
          ? f.ownerType
          : (checklist?.ownerType ?? f.ownerType),
      permissions: (f.permissions ??
        checklist?.permissions) as Plan['permissions'],
      status:
        f.status ??
        (enabled != null ? (enabled ? 'ACTIVE' : 'INACTIVE') : undefined) ??
        (disabled != null ? (disabled ? 'INACTIVE' : 'ACTIVE') : undefined) ??
        (checklist?.disabled != null
          ? checklist.disabled
            ? 'INACTIVE'
            : 'ACTIVE'
          : undefined) ??
        'ACTIVE',
      totalSteps: f.totalSteps ?? stepsTotal ?? checklist?.stepsTotal,
    }),
  );

/**
 * Temporary shim for the Plan type convergence migration.
 * Bridges PlanViewModel / AnyHybridPlan shapes to the canonical Plan type.
 *
 * Remove once the Reports and ManagePlans data pipelines are migrated to
 * return Plan[] directly from the XP API.
 *
 * @deprecated
 */
export const coerceToPlan = (vm: unknown): Plan => planVmSchema.parse(vm);

const complianceStatusToUserPlanStatus = (
  status?: string,
): UserPlanStatus['planStatus'] => {
  if (
    status === Constants.LEARN_UPON_TRAINING_PLANS.PASSED ||
    status === Constants.LEARN_UPON_TRAINING_PLANS.COMPLETED
  ) {
    return 'COMPLETED';
  }
  return 'NOT_STARTED';
};

export type ComplianceUserPlanStatus = UserPlanStatus & {
  percentComplete?: number;
  finalScore?: number;
  certificateExpiration?: string;
  isFailed?: boolean;
};

/**
 * Temporary shim for the Plan type convergence migration.
 * Bridges ComplianceCourseEnrollmentDto to the canonical UserPlanStatus type
 * so all plan card consumers can work with a single shape.
 *
 * Remove once the compliance plan pipeline returns UserPlanStatus directly.
 *
 * @deprecated
 */
export const complianceEnrollmentToUserPlanStatus = (
  enrollment: ComplianceCourseEnrollmentDto,
): ComplianceUserPlanStatus => ({
  category: Constants.PLAN_CATEGORIES.COMPLIANCE,
  completedSteps: 0,
  completionDate:
    enrollment.completedDate ?? enrollment.mostRecentCompletedDate,
  dueDate: enrollment.dueDate,
  planCreated: enrollment.startedDate,
  planId: enrollment.pathwayCourseId ?? '',
  planName: enrollment.courseName ?? {},
  planStatus: complianceStatusToUserPlanStatus(enrollment.status),
  planType: 'COMPLIANCE',
  timeTakenInSeconds: (enrollment.duration ?? 0) * 60,
  totalSteps: 0,
  percentComplete: enrollment.percentComplete ?? 0,
  finalScore: enrollment.finalScore,
  certificateExpiration: enrollment.certificate?.expiration,
  isFailed: enrollment.status === Constants.LEARN_UPON_TRAINING_PLANS.FAILED,
});
